import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, Store, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const STORAGE_KEY = "hijabi_coming_soon_shown";
const POPUP_SEEN_KEY = "hijabi_popup_seen"; // shared 30-day TTL across all marketing popups
const POPUP_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const COOKIE_CONSENT_KEY = "cookie_consent";
const ACTIVE_MODAL_KEY = "hijabi_modal_active";
const SHOW_DELAY_MS = 5000; // wait 5s after load (mobile UX requirement)

const isPopupSuppressed = (): boolean => {
  try {
    const ts = localStorage.getItem(POPUP_SEEN_KEY);
    if (!ts) return false;
    const seenAt = parseInt(ts, 10);
    if (!Number.isFinite(seenAt)) return false;
    return Date.now() - seenAt < POPUP_TTL_MS;
  } catch {
    return false;
  }
};

const ComingSoonModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    if (isPopupSuppressed()) return; // 30-day cross-popup TTL

    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;

    // Wait until the cookie banner has been dismissed (consent recorded),
    // then wait an additional 5s minimum before opening this modal.
    // This prevents the visual stack of cookie banner + modal + 2nd popup.
    const tryOpen = () => {
      if (sessionStorage.getItem(ACTIVE_MODAL_KEY)) return; // another popup is up
      if (localStorage.getItem(STORAGE_KEY)) return;
      if (isPopupSuppressed()) return;
      sessionStorage.setItem(ACTIVE_MODAL_KEY, "coming_soon");
      setOpen(true);
    };

    const startTimerOnce = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(tryOpen, SHOW_DELAY_MS);
    };

    if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
      startTimerOnce();
    } else {
      intervalId = setInterval(() => {
        if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
          startTimerOnce();
          clearInterval(intervalId);
        }
      }, 500);
    }

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    localStorage.setItem(POPUP_SEEN_KEY, Date.now().toString());
    sessionStorage.removeItem(ACTIVE_MODAL_KEY);
    setOpen(false);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email, source: "coming_soon_modal" });

      if (error) {
        if (error.code === "23505") {
          toast({
            title: "Already subscribed!",
            description: "You're already on our list. We'll keep you updated!",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "You're on the list!",
          description: "We'll notify you when we launch.",
        });
      }
      setEmail("");
      handleClose();
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent 
        className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button.absolute]:hidden"
        aria-describedby="coming-soon-description"
      >
        <DialogTitle className="sr-only">Coming Soon - America's First Modest Fashion Marketplace</DialogTitle>
        <div className="relative bg-gradient-to-br from-plum via-plum/95 to-plum-dark rounded-2xl p-6 sm:p-8 text-white">
          {/* Close button - 44x44px touch target for accessibility */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 z-20 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gold/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blush/20 rounded-full blur-3xl" />
          </div>

          {/* Content */}
          <div className="relative z-10 text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4 text-gold" />
              Coming Soon
            </div>

            <div className="space-y-3">
              <h2 className="text-[22px] sm:text-3xl font-serif font-bold leading-tight">
                America's First & Biggest Marketplace for Modesty Brands
              </h2>
              <p id="coming-soon-description" className="text-white/90 text-[15px] sm:text-base max-w-md mx-auto">
                Connecting modest fashion designers with conscious shoppers. Be part of something beautiful.
              </p>
            </div>

            {/* CTAs */}
            <div className="space-y-4 pt-2">
              {/* Vendor CTA */}
              <Link to="/vendor/register" onClick={handleClose}>
                <Button
                  variant="secondary"
                  className="w-full bg-white text-plum hover:bg-white/90 font-semibold py-6"
                >
                  <Store className="h-5 w-5 mr-2" />
                  Become a Vendor
                </Button>
              </Link>

              {/* Divider */}
              <div className="flex items-center gap-3 text-white/50 text-sm">
                <div className="flex-1 h-px bg-white/20" />
                <span>or stay updated</span>
                <div className="flex-1 h-px bg-white/20" />
              </div>

              {/* Newsletter form */}
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white text-foreground border-0 h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gold hover:bg-gold/90 text-plum font-semibold h-12 px-6"
                >
                  {loading ? "..." : "Join"}
                </Button>
              </form>

              <p className="text-white/50 text-xs">
                Join our newsletter for exclusive early access and updates.
              </p>

              <button
                type="button"
                onClick={handleClose}
                className="text-white/70 hover:text-white text-xs underline underline-offset-4 mt-1"
              >
                No thanks, continue browsing
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComingSoonModal;
